import { formatCentsValue, formatMoney } from '@/lib/utils/formatAmount';
export const formatPercentage = (percentage: number): string =>
  `${formatMoney(percentage, { decimals: 2 })}%`;

export const formatShares = (shares: number): string => formatMoney(shares, { decimals: 2 });

export const formatSharesOrDash = (shares: number | null | undefined): string =>
  shares == null ? '-' : formatShares(shares);

export const formatNumber = (num: number): string => {
  if (isNaN(num) || !isFinite(num)) {
    return '0';
  }
  if (Number.isInteger(num)) {
    return num.toString();
  }
  // Round to 2 decimal places and remove trailing zeros
  const rounded = Math.round(num * 100) / 100;
  return parseFloat(rounded.toFixed(2)).toString();
};

export const formatPriceInCents = (priceInDollars: number): string =>
  formatCentsValue(priceInDollars * 100);

export const formatDate = (date: Date): string => {
  const day = date.getUTCDate();
  const month = date.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' }).toUpperCase();
  const year = date.getUTCFullYear();
  return `${day}-${month}-${year}`;
};

export const formatTime = (date: Date): string => {
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  return `${hours}:${minutes} UTC`;
};

export const formatExpirationDate = (expireAt: string | null | undefined): string => {
  if (!expireAt) return '-';

  try {
    const expireDate = new Date(expireAt);
    const hours = expireDate.getUTCHours();
    const minutes = expireDate.getUTCMinutes();
    const seconds = expireDate.getUTCSeconds();

    // Check if it's end of day (23:59:59 or 23:59) AND it's today
    const today = new Date();
    const isToday =
      expireDate.getUTCDate() === today.getUTCDate() &&
      expireDate.getUTCMonth() === today.getUTCMonth() &&
      expireDate.getUTCFullYear() === today.getUTCFullYear();

    if (hours === 23 && minutes === 59 && (seconds === 59 || seconds === 0) && isToday) {
      return 'End of the day';
    }

    const day = expireDate.getDate();
    const month = expireDate.toLocaleString('en-US', { month: 'long' });
    const year = expireDate.getFullYear();
    const hoursStr = String(hours).padStart(2, '0');
    const minutesStr = String(minutes).padStart(2, '0');
    return `${day} ${month} ${year},\n${hoursStr}:${minutesStr} UTC`;
  } catch {
    return '-';
  }
};

export const truncateId = (id: string): string => {
  return id.slice(0, 8);
};

export const formatMarketPlaceholder = (eventId: string): string => {
  return `Event ${eventId.slice(0, 8)}...`;
};

export const formatOrderType = (type: string): string => {
  return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
};

export const priceToCents = (price: number): number => Number(formatCentsValue(price * 100));

export const formatSide = (side: 'BUY' | 'SELL'): 'Buy' | 'Sell' => {
  return side === 'BUY' ? 'Buy' : 'Sell';
};

const DEFAULT_AVATAR = '/evfallback.webp';

export const resolveAvatar = (eventIcon?: string): string => {
  return eventIcon || DEFAULT_AVATAR;
};

/**
 * Translate position/outcome tokens (YES/NO, GREEN/RED, etc.) using i18n.
 */
export const translatePosition = (outcome: string, t: (key: string) => string): string => {
  const key = outcome.toLowerCase();
  const positionKeys = ['yes', 'no', 'green', 'red', 'up', 'down'] as const;
  if (positionKeys.includes(key as (typeof positionKeys)[number])) {
    return t(`table.positions.${key as (typeof positionKeys)[number]}`);
  }
  return outcome;
};

/** Translate an order-type label (Limit/Market/Settlement); unknown types pass through. */
export const translateOrderType = (orderType: string, t: (key: string) => string): string => {
  const key = orderType.toLowerCase();
  const orderTypeKeys = ['limit', 'market', 'settlement'] as const;
  if (orderTypeKeys.includes(key as (typeof orderTypeKeys)[number])) {
    return t(`orderTypes.${key as (typeof orderTypeKeys)[number]}`);
  }
  return orderType;
};

interface ShareFiguresRow {
  side: 'Buy' | 'Sell';
  orderType: string;
  executionPrice: number;
  value: number;
  shares: number;
  outcomeStatus?: string;
  isWin?: boolean;
  avgEntryPrice?: number;
  cost?: number;
}

// isWin is a tri-state: true/false is a real win/loss, undefined is a draw or
// not-yet-known outcome — only assert a 100¢/0¢ exit price when we actually know.
export function settlementExitPrice(isWin: boolean | undefined): number | undefined {
  if (isWin === true) return 100;
  if (isWin === false) return 0;
  return undefined;
}

// isWin, avgEntryPrice and cost are pending backend rollout (see BACKEND_TODO.md)
// — no frontend inference from price/pnl. Until the backend sends them: Sell
// keeps a single price (no Entry → Exit split), Settlement shows 0 stake.
export function buildShareFigures(row: ShareFiguresRow): {
  entryPrice: number;
  exitPrice?: number;
  myBet: number;
} {
  if (row.orderType === 'Settlement') {
    const cost = row.cost ?? 0;
    return {
      entryPrice: row.shares > 0 ? Math.round((cost / row.shares) * 100) : 0,
      exitPrice: settlementExitPrice(row.isWin),
      myBet: cost,
    };
  }

  if (row.side === 'Sell' && row.avgEntryPrice !== undefined) {
    return { entryPrice: row.avgEntryPrice, exitPrice: row.executionPrice, myBet: row.value };
  }

  if (row.outcomeStatus === 'RESOLVED') {
    return {
      entryPrice: row.executionPrice,
      exitPrice: settlementExitPrice(row.isWin),
      myBet: row.value,
    };
  }

  return { entryPrice: row.executionPrice, myBet: row.value };
}

export const resolveOutcomeName = (
  outcomeName: string,
  tokenName: string,
  eventTitle: string,
  isPrimary: boolean,
): string => {
  const name = outcomeName === eventTitle ? tokenName : outcomeName;
  return name || (isPrimary ? 'Yes' : 'No');
};
